// packages/ui/src/SaveStatusIndicator.tsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, AlertTriangle, Loader } from 'lucide-react';
import clsx from 'clsx';

export type SaveStatus = 'saved' | 'saving' | 'error' | 'unsaved';
type TranslationNamespace = string | readonly string[];

interface SaveStatusIndicatorProps {
  status: SaveStatus;
  className?: string;
  translationNs?: TranslationNamespace;
  labels?: Partial<Record<SaveStatus, string>>;
  labelKeys?: Partial<Record<SaveStatus, string>>;
}

export const SaveStatusIndicator: React.FC<SaveStatusIndicatorProps> = ({
  status,
  className,
  translationNs = 'common',
  labels,
  labelKeys,
}) => {
  const { t } = useTranslation(translationNs);

  const config = {
    saved: {
      Icon: Check,
      defaultTextKey: 'creatorStudio:saveStatus.saved',
      colorClass: 'text-slate-500 [.theme-liquid-glass_&]:text-white/70',
      iconClassName: '',
    },
    saving: {
      Icon: Loader,
      defaultTextKey: 'creatorStudio:saveStatus.saving',
      colorClass: 'text-indigo-600 [.theme-liquid-glass_&]:text-violet-300',
      iconClassName: 'animate-spin',
    },
    unsaved: {
        Icon: AlertTriangle,
        defaultTextKey: 'creatorStudio:saveStatus.unsaved',
        colorClass: 'text-amber-600 [.theme-liquid-glass_&]:text-amber-300',
        iconClassName: '',
    },
    error: {
      Icon: AlertTriangle,
      defaultTextKey: 'creatorStudio:saveStatus.error',
      colorClass: 'text-red-600 [.theme-liquid-glass_&]:text-rose-400',
      iconClassName: '',
    },
  }[status];
  const label = labels?.[status] ?? t(labelKeys?.[status] ?? config.defaultTextKey);

  return (
    <div aria-live="polite" className={clsx('flex items-center gap-1.5 text-xs font-medium', config.colorClass, className)}>
      <config.Icon size={14} className={config.iconClassName} />
      <span>{label}</span>
    </div>
  );
};
